import { useEffect, useRef, useState } from 'react';
import './Editorial.css';

const photographs = [
  { key: 'exterior', title: 'The arrival', detail: 'Stone, shade, and a quiet first impression.', alt: 'Aurelia House exterior, a modern Mediterranean home with pale stone walls and warm architectural light.' },
  { key: 'living', title: 'The living room', detail: 'A generous room, held between light and landscape.', alt: 'The open living room of Aurelia House, with warm neutral furnishings and large glass walls.' },
  { key: 'kitchen', title: 'The heart of the home', detail: 'Natural materials. An unhurried daily ritual.', alt: 'Aurelia House kitchen with a sculptural island and warm natural finishes.' },
  { key: 'detail', title: 'Material & light', detail: 'A closer study of the living room’s natural textures.', alt: 'A cropped detail of the Aurelia House living room, showing its warm natural materials.' },
  { key: 'terrace', title: 'The reflection garden', detail: 'The arrival courtyard, seen a little closer.', alt: 'A closer crop of the Aurelia House exterior, showing its reflection pool and planted arrival courtyard.' },
];

function Arrow({ direction = 'right', className = '' }) {
  return <span className={`ed-arrow ${className}`} aria-hidden="true">{direction === 'left' ? '←' : '→'}</span>;
}

function Photo({ name, className = '', alt, sizes = '(max-width: 700px) 100vw, 90vw' }) {
  const item = photographs.find((photograph) => photograph.key === name);
  return <picture className={`ed-photo ${className}`}>
    <source media="(max-width: 640px)" srcSet={`/media/${name}-small.webp`} />
    <img src={`/media/${name}.webp`} alt={alt ?? item.alt} width="1600" height="900" loading="lazy" decoding="async" sizes={sizes} />
  </picture>;
}

export default function Editorial() {
  const [selectedPhotograph, setSelectedPhotograph] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [briefReady, setBriefReady] = useState(false);
  const editorial = useRef(null);
  const gallery = useRef(null);
  const galleryTrigger = useRef(null);
  const activePhotograph = photographs[selectedPhotograph];

  useEffect(() => {
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motionPreference.matches || !('IntersectionObserver' in window)) return undefined;
    const targets = [...editorial.current.querySelectorAll('h2, .ed-lead, .ed-section-line, .ed-interior-pair figcaption, .ed-journal-title')];
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('ed-reveal-pending');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12 });
    targets.forEach((target) => {
      if (target.getBoundingClientRect().top < window.innerHeight) return;
      target.classList.add('ed-reveal', 'ed-reveal-pending');
      observer.observe(target);
    });
    const revealImmediately = () => {
      if (!motionPreference.matches) return;
      observer.disconnect();
      targets.forEach((target) => target.classList.remove('ed-reveal-pending'));
    };
    motionPreference.addEventListener('change', revealImmediately);
    return () => {
      observer.disconnect();
      motionPreference.removeEventListener('change', revealImmediately);
      targets.forEach((target) => target.classList.remove('ed-reveal', 'ed-reveal-pending'));
    };
  }, []);

  useEffect(() => {
    if (!galleryOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [galleryOpen]);

  function openGallery(index, event) {
    galleryTrigger.current = event.currentTarget;
    setSelectedPhotograph(index);
    gallery.current.showModal();
    setGalleryOpen(true);
  }

  function closeGallery() {
    gallery.current.close();
  }

  function afterGalleryClose() {
    setGalleryOpen(false);
    galleryTrigger.current?.focus({ preventScroll: true });
  }

  function changePhotograph(amount) {
    setSelectedPhotograph((current) => (current + amount + photographs.length) % photographs.length);
  }

  function handleGalleryKeys(event) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      changePhotograph(event.key === 'ArrowRight' ? 1 : -1);
    }
  }

  function downloadBrief(event) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const brief = [
      'AURELIA HOUSE — PRIVATE VIEWING INQUIRY BRIEF',
      '',
      `Name: ${String(values.get('name')).trim()}`,
      `Email: ${String(values.get('email')).trim()}`,
      `Interest: ${values.get('interest')}`,
      '',
      'Personal note:',
      String(values.get('message') || '').trim() || 'No additional note.',
      '',
      'Aurelia House is a fictional architectural concept, not an available property listing.',
      'This file was prepared locally on your device. No information was sent and no viewing has been booked.',
    ].join('\n');
    const fileUrl = URL.createObjectURL(new Blob([brief], { type: 'text/plain;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = fileUrl;
    anchor.download = 'aurelia-house-inquiry.txt';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1500);
    setBriefReady(true);
  }

  return <div className="editorial" ref={editorial}>
    <section className="ed-residence ed-container" id="residence" aria-labelledby="ed-residence-title">
      <div className="ed-section-line"><span className="ed-eyebrow">The residence</span><span className="ed-folio">Featured residence · Aurelia House</span></div>
      <div className="ed-intro-grid">
        <h2 id="ed-residence-title">Space to live.<br /><em>Room to be.</em></h2>
        <div className="ed-intro-copy">
          <p className="ed-lead">A home that opens to the light,<br className="ed-desktop-break" /> and brings you back to yourself.</p>
          <p>Conceived as one continuous experience, Aurelia House moves from a sheltered arrival to luminous, open living spaces. Pale stone, warm timber, and disappearing boundaries bring the landscape into the everyday.</p>
          <span className="ed-concept-note">An imagined Mediterranean residence.</span>
        </div>
      </div>
      <figure className="ed-feature-image" id="featured-residence" aria-label="Featured residence: Aurelia House">
        <Photo name="living" />
        <figcaption><span>A sense of openness</span><span>Living at Aurelia</span></figcaption>
      </figure>
      <div className="ed-residence-facts" aria-label="Illustrative concept highlights">
        <div><strong>04</strong><span>Private suites</span></div>
        <div><strong>620 <small>m²</small></strong><span>Proposed interior</span></div>
        <div><strong>18 <small>m</small></strong><span>Proposed lap pool</span></div>
        <p>Considered in every detail.<br />Generous in every dimension.<span>Illustrative concept specifications</span></p>
      </div>
    </section>

    <section className="ed-architecture" id="architecture" aria-labelledby="ed-architecture-title">
      <div className="ed-container ed-architecture-grid">
        <div className="ed-architecture-copy">
          <span className="ed-eyebrow">The architecture</span>
          <h2 id="ed-architecture-title">Drawn from<br /><em>the landscape.</em></h2>
          <p>Strong horizontal lines. The depth of a shaded threshold. Walls that frame a view instead of closing it away.</p>
          <p>The design brings a quiet, contemporary sensibility to the Mediterranean home. Natural textures soften its geometry; a restrained palette lets the changing light become the decoration.</p>
          <a href="#gallery" className="ed-text-link">Explore the details <Arrow /></a>
        </div>
        <figure className="ed-architecture-image">
          <Photo name="exterior" />
          <figcaption>Where solidity meets lightness.</figcaption>
        </figure>
      </div>
    </section>

    <section className="ed-interiors ed-container" aria-labelledby="ed-interiors-title">
      <div className="ed-section-line"><span className="ed-eyebrow">Life within</span><span className="ed-folio">The interiors</span></div>
      <div className="ed-interiors-heading">
        <h2 id="ed-interiors-title">The art of<br /><em>feeling at home.</em></h2>
        <p>Spaces for coming together.<br />Places to quietly retreat.<br />A natural rhythm between the two.</p>
      </div>
      <div className="ed-interior-pair">
        <figure className="ed-kitchen-figure">
          <Photo name="kitchen" sizes="(max-width: 700px) 100vw, 55vw" />
          <figcaption><span className="ed-eyebrow">Gather</span><h3>Everyday, beautifully considered.</h3><p>A sculptural kitchen anchors the shared spaces, making a simple morning coffee feel like a small occasion.</p></figcaption>
        </figure>
        <figure className="ed-suite-figure">
          <Photo name="detail" sizes="(max-width: 700px) 100vw, 40vw" />
          <figcaption><span className="ed-eyebrow">Texture</span><h3>A quieter material language.</h3><p>A closer look at the living space reveals the warmth of timber, the tactility of stone, and the softness of natural light.</p></figcaption>
        </figure>
      </div>
    </section>

    <section className="ed-outdoors" aria-labelledby="ed-outdoors-title">
      <figure className="ed-terrace-image"><Photo name="terrace" sizes="100vw" /></figure>
      <div className="ed-container ed-outdoors-content">
        <div><span className="ed-eyebrow">Beyond the walls</span><h2 id="ed-outdoors-title">A little closer<br /><em>to the elements.</em></h2></div>
        <div className="ed-amenities">
          <p>The reflection garden pictured above offers a quiet moment at the arrival. Beyond it, the proposed amenity programme imagines space for the rituals that make a day your own.</p>
          <dl>
            <div><dt>Water & wellbeing</dt><dd>An 18-metre lap pool and a dedicated space for movement.</dd></div>
            <div><dt>Open-air living</dt><dd>A shaded dining terrace, outdoor lounge, and garden paths.</dd></div>
            <div><dt>Private by design</dt><dd>A sheltered arrival and planted edges around the home.</dd></div>
          </dl>
        </div>
      </div>
    </section>

    <section className="ed-location ed-container" id="location" aria-labelledby="ed-location-title">
      <div className="ed-location-heading"><span className="ed-eyebrow">The setting</span><h2 id="ed-location-title">Somewhere<br /><em>time slows.</em></h2></div>
      <div className="ed-location-copy"><p className="ed-lead">An imagined hillside.<br />An unmistakably Mediterranean spirit.</p><p>Olive-green horizons, the warmth of stone underfoot, and long evenings spent outside. Aurelia is conceived for a coastal landscape where architecture takes its cues from the seasons.</p><p className="ed-small-note">This is an architectural concept. No physical address, travel distances, or property availability are represented.</p></div>
      <div className="ed-location-rule"><span>Light</span><span>Landscape</span><span>A quieter rhythm</span></div>
    </section>

    <section className="ed-specifications" aria-labelledby="ed-specifications-title">
      <div className="ed-container ed-specs-grid">
        <div><span className="ed-eyebrow">At a glance</span><h2 id="ed-specifications-title">A considered<br /><em>composition.</em></h2><p className="ed-small-note">Illustrative proposed specifications.<br />Areas and amenities are part of the design concept.</p></div>
        <div className="ed-specs-table">
          <div className="ed-specs-group"><h3>The spaces</h3><dl><div><dt>Interior area</dt><dd>620 m²</dd></div><div><dt>Land area</dt><dd>2,100 m²</dd></div><div><dt>Private suites</dt><dd>4</dd></div><div><dt>Lap pool</dt><dd>18 m</dd></div></dl></div>
          <div className="ed-specs-group"><h3>The material palette</h3><dl><div><dt>Architecture</dt><dd>Pale natural stone</dd></div><div><dt>Interior warmth</dt><dd>Timber & tactile textiles</dd></div><div><dt>Connection</dt><dd>Full-height glazing</dd></div><div><dt>Landscape</dt><dd>Mediterranean planting</dd></div></dl></div>
        </div>
      </div>
    </section>

    <section className="ed-gallery ed-container" id="gallery" aria-labelledby="ed-gallery-title">
      <div className="ed-gallery-heading"><div><span className="ed-eyebrow">A closer look</span><h2 id="ed-gallery-title">In the <em>details.</em></h2></div><p>Three scenes. Two studies.<br />One Aurelia House.</p></div>
      <div className="ed-gallery-grid">
        {photographs.map((photograph, index) => <button key={photograph.key} className={`ed-gallery-tile ed-gallery-tile-${index + 1}`} onClick={(event) => openGallery(index, event)} aria-labelledby={`gallery-caption-${photograph.key}`} aria-haspopup="dialog">
          <Photo name={photograph.key} alt="" sizes="(max-width: 700px) 100vw, 50vw" />
          <span className="ed-gallery-caption"><span id={`gallery-caption-${photograph.key}`}>{photograph.title}</span><span className="ed-gallery-expand" aria-hidden="true">↗</span></span>
        </button>)}
      </div>
      <dialog ref={gallery} className="ed-gallery-dialog" aria-labelledby="ed-dialog-title" aria-describedby="ed-dialog-caption" onClose={afterGalleryClose} onKeyDown={handleGalleryKeys} onClick={(event) => { if (event.target === event.currentTarget) closeGallery(); }}>
        <div className="ed-dialog-inner">
          <div className="ed-dialog-top"><span className="ed-eyebrow">Aurelia House · The collection</span><button type="button" className="ed-dialog-close" aria-label="Close gallery" onClick={closeGallery} autoFocus><span aria-hidden="true">×</span></button></div>
          <picture className="ed-dialog-picture"><source media="(max-width: 640px)" srcSet={`/media/${activePhotograph.key}-small.webp`} /><img src={`/media/${activePhotograph.key}.webp`} alt={activePhotograph.alt} width="1600" height="900" /></picture>
          <div className="ed-dialog-bottom"><div aria-live="polite" aria-atomic="true"><h3 id="ed-dialog-title">{activePhotograph.title}</h3><p id="ed-dialog-caption">{activePhotograph.detail}</p></div><div className="ed-dialog-controls"><button type="button" aria-label="Previous photograph" onClick={() => changePhotograph(-1)}><Arrow direction="left" /></button><span aria-label={`Photograph ${selectedPhotograph + 1} of ${photographs.length}`}>{String(selectedPhotograph + 1).padStart(2, '0')} / 05</span><button type="button" aria-label="Next photograph" onClick={() => changePhotograph(1)}><Arrow /></button></div></div>
        </div>
      </dialog>
    </section>

    <section className="ed-journal ed-container" id="journal" aria-labelledby="ed-journal-title">
      <span className="ed-eyebrow" id="ed-journal-title">A note on design</span>
      <details><summary><span className="ed-journal-title">The luxury of<br /><em>leaving room.</em></span><span className="ed-journal-action">Read the design note <Arrow /></span></summary><div className="ed-journal-body"><p className="ed-lead">Some of the most meaningful things a home can offer are the things it leaves open.</p><p>A view without interruption. A room with enough space to breathe. An afternoon with no reason to leave. The idea behind Aurelia House began with these small, everyday freedoms.</p><p>Its architecture is conceived as a sequence rather than a collection of rooms. A solid, sheltered entrance gives way to an open foyer; beyond it, the living space draws the eye toward the garden. Each threshold changes the quality of the light and the pace of the experience.</p><p>The materials follow the same principle of restraint. Stone lends weight, timber brings warmth, and glass allows the landscape to remain present. There is no need for every surface to speak at once.</p><p>This is a fictional design study in a quieter form of luxury: a home measured as much by how it feels as by what it contains.</p><span className="ed-journal-signoff">The Aurelia design concept</span></div></details>
    </section>

    <section className="ed-office" id="office" aria-labelledby="ed-office-title">
      <div className="ed-container ed-office-grid">
        <div className="ed-office-copy"><span className="ed-eyebrow">Private introductions</span><h2 id="ed-office-title">Imagine<br /><em>your life here.</em></h2><p>Gather your thoughts for a private viewing enquiry. Your brief is prepared on your device, ready for you to keep.</p><p className="ed-office-disclosure">Aurelia House is a fictional residence. This demonstration downloads a text file; it does not send your details or book a viewing.</p></div>
        <form className="ed-inquiry" onSubmit={downloadBrief} onChange={() => setBriefReady(false)}>
          <div className="ed-form-row"><label htmlFor="aurelia-name">Your name <span aria-hidden="true">*</span></label><input id="aurelia-name" name="name" type="text" autoComplete="name" placeholder="Full name" required maxLength="120" /></div>
          <div className="ed-form-row"><label htmlFor="aurelia-email">Email address <span aria-hidden="true">*</span></label><input id="aurelia-email" name="email" type="email" autoComplete="email" placeholder="you@example.com" required maxLength="254" /></div>
          <div className="ed-form-row"><label htmlFor="aurelia-interest">I'm interested in</label><select id="aurelia-interest" name="interest" defaultValue="A private viewing"><option>A private viewing</option><option>The architectural concept</option><option>The interior design</option></select></div>
          <div className="ed-form-row"><label htmlFor="aurelia-message">A personal note <span className="ed-optional">(optional)</span></label><textarea id="aurelia-message" name="message" rows="3" maxLength="2000" placeholder="What caught your eye?" /></div>
          <div className="ed-form-footer"><span>* Required fields</span><button className="ed-submit" type="submit">Download inquiry brief <Arrow /></button></div>
          <p className="ed-form-status" role="status" aria-live="polite">{briefReady ? 'Your brief is ready. The text-file download has started. Nothing has been sent.' : 'Your details stay in this browser until you download them.'}</p>
        </form>
      </div>
    </section>

    <footer className="ed-footer ed-container"><a className="ed-footer-wordmark" href="#top" title="Return to the beginning">AURELIA{' '}<span>HOUSE</span></a><p>Architecture for<br />a more considered life.</p><div className="ed-footer-links"><a href="#residence">The residence</a><a href="#architecture">Architecture</a><a href="#office">Private introductions</a></div><div className="ed-footer-bottom"><span>© {new Date().getFullYear()} Aurelia House · A residential concept</span><span>AI-generated visualisations. Illustrative specifications.</span><span className="ed-credit">Designed and built by <a href="/nechecode/">@nechecode</a></span><a href="#top">Back to the beginning ↑</a></div></footer>
  </div>;
}
